import { callApi } from '@/api/callApi';
import { API_ENDPOINT, API_URL_PRODUCT_SERVICE } from '@/api/constant';
import CommonTable, {
  TableColumn,
} from '@/components/common/commonTable/CommonTable';
import Pagination from '@/components/common/Pagination';
import Card from '@/components/UI/Card';
import { useDebounce } from '@/hooks/useDebounce';
import { useSelectableTable } from '@/hooks/useSelectableTable';
import { PageInfo } from '@/interfaces/common.interface';
import { GetFilterUrl } from '@/utility/helper';
import React, { useEffect, useState } from 'react';
import FormField from '../UI/FormField';
import ImagePreview from '../common/media/ImagePreview';
import PageHeading from '../common/PageHeading';
import {
  IBrandsResponse,
  IBrandsResponseList,
} from '@/interfaces/brandsResponse.interface';

interface BrandTableProp {
  onSelectBrand?: (selected: IBrandsResponse[]) => void;
  selectionMode?: 'single' | 'multiple';
  initialSelectedIds?: Array<string | number>;
}

interface BrandStates {
  isLoading: boolean;
  brandStatus: string;
  brands: IBrandsResponse[];
  pageInfo: PageInfo;
}

const BrandTable: React.FC<BrandTableProp> = ({
  onSelectBrand,
  selectionMode = 'multiple',
  initialSelectedIds = [],
}) => {
  const [searchValue, setSearchValue] = useState('');
  const debounceValue = useDebounce(searchValue);
  const [selectedBrands, setSelectedBrands] = useState<
    Map<string, IBrandsResponse>
  >(new Map());

  const [states, setStates] = useState<BrandStates>({
    isLoading: false,
    brandStatus: 'active',
    brands: [],
    pageInfo: {
      total: 0,
      totalPages: 0,
      limit: 10,
      page: 1,
    },
  });

  const handleChangeState = (newState: Partial<BrandStates>) => {
    setStates((prev) => ({ ...prev, ...newState }));
  };

  /* ------------------ API ------------------ */
  const fetchBrands = async () => {
    try {
      handleChangeState({ isLoading: true });

      const query = GetFilterUrl({
        page: states.pageInfo.page,
        limit: states.pageInfo.limit,
        status: states.brandStatus,
        search: debounceValue,
      });

      const PATH = `${API_URL_PRODUCT_SERVICE}${API_ENDPOINT.brands}${
        query ? `?${query}` : ''
      }`;

      const res = await callApi<IBrandsResponseList>(PATH, { method: 'GET' });

      handleChangeState({
        brands: res.data,
        pageInfo: {
          limit: +res.pageInfo.limit,
          page: +res.pageInfo.page,
          total: +res.pageInfo.total,
          totalPages: +res.pageInfo.totalPages,
        },
        isLoading: false,
      });
    } catch (error) {
      console.error(error);
      handleChangeState({ isLoading: false });
    }
  };

  useEffect(() => {
    fetchBrands();
  }, [
    states.pageInfo.page,
    states.pageInfo.limit,
    states.brandStatus,
    debounceValue,
  ]);

  /* ------------------ Selection ------------------ */
  const { selectedItems, handleSelectAll, handleSelectItem, clearSelection } =
    useSelectableTable(states.brands);

  const handleSelectBrand = (brand: IBrandsResponse) => {
    const isSelected = selectedItems.has(brand.id);

    if (selectionMode === 'single') {
      clearSelection();

      handleSelectItem(brand, true);

      setSelectedBrands(new Map([[brand.id, brand]]));
      return;
    }

    handleSelectItem(brand, !isSelected);

    setSelectedBrands((prev) => {
      const next = new Map(prev);

      if (isSelected) {
        next.delete(brand.id);
      } else {
        next.set(brand.id, brand);
      }

      return next;
    });
  };

  /* Emit selection to parent */
  useEffect(() => {
    onSelectBrand?.([...selectedBrands.values()]);
  }, [selectedBrands]);

  /* Initial selection (single or multiple) */
  useEffect(() => {
    if (!initialSelectedIds.length || !states.brands.length) return;

    states.brands.forEach((brand) => {
      if (initialSelectedIds.includes(brand.id)) {
        handleSelectItem(brand, true);

        setSelectedBrands((prev) => {
          const next = new Map(prev);
          next.set(brand.id, brand);
          return next;
        });
      }
    });
  }, [states.brands]);
  /* ------------------ Columns ------------------ */
  const columns: TableColumn<IBrandsResponse>[] = [
    {
      key: 'name',
      header: 'Brand',
      render: (item) => (
        <ImagePreview
          src={`${API_URL_PRODUCT_SERVICE}/${item?.media?.[0]?.url}`}
          alt=""
          text={item.name}
        />
      ),
    },
    {
      key: 'description',
      header: 'Description',
      render: (item) => (
        <span className="inline-block max-w-[250px] truncate font-medium">
          {item.description}
        </span>
      ),
    },
    {
      key: 'productCount',
      header: 'Products',
    },
  ];

  /* ------------------ Pagination ------------------ */
  const handleChangePage = (page: number) => {
    handleChangeState({
      pageInfo: { ...states.pageInfo, page },
    });
  };

  const handleSizeChange = (limit: number) => {
    handleChangeState({
      pageInfo: { ...states.pageInfo, limit },
    });
  };

  /* ------------------ UI ------------------ */
  return (
    <Card>
      <PageHeading text="Brands" />
      <div className="mb-2">
        <FormField
          placeholder="Search for the brands..."
          onChange={(e) => setSearchValue(e.target.value)}
        />
      </div>

      <CommonTable
        columns={columns}
        data={states.brands}
        showCheckbox
        selectedItems={selectedItems}
        onSelectAll={
          selectionMode === 'multiple'
            ? (checked) => {
                handleSelectAll(checked);

                setSelectedBrands((prev) => {
                  const next = new Map(prev);

                  states.brands.forEach((brand) => {
                    if (checked) {
                      next.set(brand.id, brand);
                    } else {
                      next.delete(brand.id);
                    }
                  });

                  return next;
                });
              }
            : undefined
        }
        onSelectItem={handleSelectBrand}
        getItemId={(item) => item.id}
        emptyMessage="No Brands Found"
        loading={states.isLoading}
      />

      <Pagination
        paginationData={states.pageInfo}
        onPageChange={handleChangePage}
        isLoading={states.isLoading}
        onPageSizeChange={handleSizeChange}
      />
    </Card>
  );
};

export default BrandTable;
